// Post-booking deliveries shared by the ordinary checkout (Payment.js) and the
// table-link page (TableLink.js): the boarding-pass ticket and the tax invoice,
// both generated in the browser and sent to the guest's WhatsApp through the
// already-deployed send-ticket / send-invoice edge functions.
//
// Moved verbatim out of Payment.js on 22-10-2026 so the link page reuses the
// exact same sends instead of a copy (GAPS.md #2: duplication is a defect).
// Behaviour is unchanged: every function here is fully NON-FATAL and meant to
// be fire-and-forget — the booking is already written in the DB, so any
// failure (generation, invalid number, send error) is only logged and must
// never undo or hide a confirmed booking, and nothing here is awaited in a
// render path.

import { supabase } from './supabase';
import { fetchCruiseTimes, fetchBoardingPoint, fetchCruiseNames } from './cruiseDetails';
import { cruiseLabel } from '../components/shared/helpers';
import { getTicketPdfBase64 } from '../utils/ticketPDF';
import { getInvoicePdfBase64 } from '../utils/invoicePDF';
import { normalizeIndianMobile } from '../utils/phone';

// Backend stores the cruise date as ISO (YYYY-MM-DD); the ticket + template
// want DD-MM-YYYY. Convert here so the generated PDF and the WhatsApp body
// values both carry the brand's DD-MM-YYYY date.
export function toDdMmYyyy(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  return (y && m && d) ? `${d}-${m}-${y}` : String(iso);
}

// 'HH:MM' 24h → 'H:MM AM/PM'. Small inlined copy of the internal app's
// TicketPrint.to12hr — used only to format reporting_time for the template.
function to12hr(hhmm) {
  if (!hhmm) return '—';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Generate the boarding-pass ticket (same PDF the internal app produces) and
// send it to the customer's WhatsApp via the ALREADY-DEPLOYED send-ticket edge
// function. Fully NON-FATAL and fire-and-forget: the booking is already written
// in the DB, so any failure here (generation, invalid number, send error) is
// only logged — it must never undo or hide the confirmed booking, and the
// confirmation screen is never blocked while this runs. Mirrors the internal
// SendTicketModal's payload shape and bodyValues order exactly.
export async function sendTicketWhatsApp(booking, reference) {
  try {
    // Only the guest phone here (no partner booking on the public site).
    const norm = normalizeIndianMobile(booking.guest_phone);
    if (!norm.valid) {
      console.error('Ticket not sent — invalid customer phone:', norm.reason);
      return;
    }

    // Build the PDF (identical to the internal download) and, in parallel,
    // source the five template body values from the SAME lookup the printed
    // ticket uses (cruiseDetails, for the booking's cruise date): boarding
    // point and reporting time (formatted via to12hr).
    const [pdf, boarding, times, names] = await Promise.all([
      getTicketPdfBase64(booking, reference || null),
      fetchBoardingPoint({ date: booking.date }),
      fetchCruiseTimes({ cruiseName: booking.cruise_type, date: booking.date }),
      fetchCruiseNames({ cruiseName: booking.cruise_type }),
    ]);

    const boardingPoint = boarding?.boarding_point || 'Guwahati';
    const reportingTime = to12hr(times?.reporting_time);
    const bodyValues = [
      booking.booking_name || 'Guest', // {{1}} name
      // {{2}} cruise — SHORT display name: WhatsApp template variables keep the
      // short name (display only; the stored booking.cruise_type is the
      // fallback and stays the key everywhere else)
      cruiseLabel(names, 'short') || booking.cruise_type || '—',
      booking.date,                    // {{3}} date (DD-MM-YYYY)
      reportingTime,                   // {{4}} reporting time
      boardingPoint,                   // {{5}} boarding point
    ];

    const { error } = await supabase.functions.invoke('send-ticket', {
      body: {
        pdfBase64: pdf.base64,
        fileName: pdf.filename,
        phoneNumbers: [norm.number],
        bodyValues,
        bookingId: booking.id,
      },
    });
    if (error) console.error('send-ticket invoke returned an error:', error.message || error);
  } catch (err) {
    // Non-fatal — the booking is confirmed regardless; staff can resend later.
    console.error('Ticket generate/send failed (booking still confirmed):', err);
  }
}

// After the booking is confirmed, create-or-fetch its tax invoice (generate-invoice
// is idempotent), build the invoice PDF, and send it to the customer's WhatsApp via
// the deployed send-invoice edge function. Fully NON-FATAL and fire-and-forget: the
// booking is already written, so any failure here (missing invoice, generation,
// invalid number, send error) is only logged — it must never undo or hide the
// confirmed booking, and is never awaited in the render path. Mirrors the ticket
// send's structure. `cruiseDate` is the DD-MM-YYYY cruise date from context.
export async function generateAndSendInvoice(bookingId, guestPhone, cruiseDate) {
  try {
    // 1) Create-or-fetch the invoice row (a retry returns the existing one).
    const { data, error } = await supabase.functions.invoke('generate-invoice', {
      body: { bookingId },
    });
    if (error) {
      console.error('generate-invoice returned an error:', error.message || error);
      return;
    }
    const invoice = data?.invoice;
    if (!invoice) {
      console.error('Invoice not sent — generate-invoice returned no invoice.');
      return;
    }

    // 2) NON-dinner invoices split into Adult/Child rows priced from the pricing
    //    table; dinner charges come from cruise_settings inside the PDF (no rates).
    let pricingRates = null;
    const isDinner = String(invoice.cruise_type || '').toLowerCase().includes('dinner');
    if (!isDinner) {
      const { data: rows } = await supabase
        .from('pricing')
        .select('passenger_type, price')
        .eq('cruise_type', invoice.cruise_type)
        .eq('deck', invoice.deck);
      pricingRates = { adult: 0, child: 0 };
      for (const r of rows || []) {
        const t = String(r.passenger_type || '').toLowerCase();
        if (t === 'adult') pricingRates.adult = Number(r.price) || 0;
        else if (t === 'child') pricingRates.child = Number(r.price) || 0;
      }
    }

    // 3) Guest-facing SHORT name for the message (template variables keep the
    //    short name; display only, invoice.cruise_type stays the key). Booking
    //    object for the PDF, from the invoice fields + the cruise date.
    const names = await fetchCruiseNames({ cruiseName: invoice.cruise_type });
    const booking = {
      cruise_type: invoice.cruise_type,
      deck: invoice.deck,
      date: cruiseDate,
      adults: invoice.adults,
      children: invoice.children,
    };

    // 4) Build the PDF (settings null → company defaults in the util).
    const pdf = await getInvoicePdfBase64(invoice, booking, null, pricingRates);

    // 5) Recipient: the customer/guest phone (same number the ticket went to).
    const norm = normalizeIndianMobile(guestPhone);
    if (!norm.valid) {
      console.error('Invoice not sent — invalid customer phone:', norm.reason);
      return;
    }
    const name = invoice.customer_name || 'Guest';

    // 6) send_invoice template body: name, invoice no, cruise, date, amount.
    const bodyValues = [
      name,                          // {{1}} recipient name
      invoice.invoice_number || '—', // {{2}} invoice number
      cruiseLabel(names, 'short') || invoice.cruise_type || '—', // {{3}} cruise (short display name)
      cruiseDate,                    // {{4}} date (DD-MM-YYYY)
      String(invoice.grand_total),   // {{5}} amount (grand total)
    ];

    // 7) Send (non-fatal).
    const { error: sendErr } = await supabase.functions.invoke('send-invoice', {
      body: {
        pdfBase64: pdf.base64,
        fileName: pdf.filename,
        phoneNumbers: [norm.number],
        bodyValues,
        bookingId,
      },
    });
    if (sendErr) console.error('send-invoice invoke returned an error:', sendErr.message || sendErr);
  } catch (err) {
    // Non-fatal — the booking is confirmed regardless; staff can resend later.
    console.error('Invoice generate/send failed (booking still confirmed):', err);
  }
}
